import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api, { getErrorMessage } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ListChecks, Waves, ChevronRight, Save, X, Minus } from 'lucide-react';

const LEVEL_COLORS = [
  'bg-red-50 border-red-200 text-red-700',
  'bg-orange-50 border-orange-200 text-orange-700',
  'bg-amber-50 border-amber-200 text-amber-700',
  'bg-yellow-50 border-yellow-200 text-yellow-700',
  'bg-lime-50 border-lime-200 text-lime-700',
  'bg-green-50 border-green-200 text-green-700',
  'bg-emerald-50 border-emerald-200 text-emerald-700',
  'bg-teal-50 border-teal-200 text-teal-700',
  'bg-cyan-50 border-cyan-200 text-cyan-700',
  'bg-sky-50 border-sky-200 text-sky-700',
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-indigo-50 border-indigo-200 text-indigo-700',
  'bg-violet-50 border-violet-200 text-violet-700',
  'bg-purple-50 border-purple-200 text-purple-700',
  'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700',
  'bg-pink-50 border-pink-200 text-pink-700',
  'bg-rose-50 border-rose-200 text-rose-700',
  'bg-slate-50 border-slate-200 text-slate-700',
  'bg-stone-50 border-stone-200 text-stone-700',
  'bg-zinc-50 border-zinc-200 text-zinc-700',
];

const makeLevels = (count) => Array.from({ length: count }, (_, i) => ({ level: i + 1, description: '' }));

export default function CriteriaPage() {
  const { childId } = useParams();
  const { user } = useAuth();
  const [criteria, setCriteria] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', levels: makeLevels(5) });
  const [loading, setLoading] = useState(true);
  const isInstructor = user?.role === 'instructor';

  const fetchCriteria = useCallback(async () => {
    try {
      const res = await api.get(`/criteria?child_id=${childId}`);
      setCriteria(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, [childId]);

  useEffect(() => { fetchCriteria(); }, [fetchCriteria]);

  const resetForm = () => {
    setForm({ title: '', description: '', levels: makeLevels(5) });
    setEditing(null);
  };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (crit) => {
    setEditing(crit.criteria_id);
    setForm({
      title: crit.title,
      description: crit.description || '',
      levels: (crit.levels || []).map((l, i) => ({ level: i + 1, description: l.description || '' })),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.levels.length < 2) { toast.error('At least 2 levels required'); return; }
    try {
      if (editing) {
        await api.put(`/criteria/${editing}`, { title: form.title, description: form.description, levels: form.levels });
        toast.success('Criteria updated!');
      } else {
        await api.post('/criteria', { child_id: childId, title: form.title, description: form.description, levels: form.levels });
        toast.success('Criteria created!');
      }
      setShowForm(false);
      resetForm();
      fetchCriteria();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save criteria'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this criteria?')) return;
    try {
      await api.delete(`/criteria/${id}`);
      toast.success('Criteria deleted');
      fetchCriteria();
    } catch { toast.error('Failed to delete'); }
  };

  const updateLevel = (idx, desc) => {
    setForm(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === idx ? { ...l, description: desc } : l)
    }));
  };

  const addLevel = () => {
    setForm(prev => ({
      ...prev,
      levels: [...prev.levels, { level: prev.levels.length + 1, description: '' }]
    }));
  };

  const removeLevel = () => {
    if (form.levels.length <= 2) return;
    setForm(prev => ({
      ...prev,
      levels: prev.levels.slice(0, -1)
    }));
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="criteria-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>Assessment Criteria</h1>
          <p className="text-slate-500 mt-1">
            {isInstructor ? 'Co-create and manage assessment criteria' : 'View assessment criteria (instructor manages)'}
          </p>
        </div>
        {isInstructor && (
          <Button data-testid="new-criteria-btn" onClick={openCreate} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold gap-2">
            <Plus className="w-4 h-4" /> New Criteria
          </Button>
        )}
      </div>

      {criteria.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ListChecks className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Criteria Yet</p>
            <p className="text-sm text-slate-400 mt-2">
              {isInstructor ? 'Create assessment criteria for this child' : 'Your instructor will set up criteria'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {criteria.map(crit => (
            <Card key={crit.criteria_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`criteria-${crit.criteria_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                    <ChevronRight className="w-5 h-5 text-[#22D3EE]" />
                    {crit.title}
                    <Badge variant="outline" className="rounded-full text-xs ml-2">{(crit.levels || []).length} levels</Badge>
                  </CardTitle>
                  {isInstructor && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#22D3EE] hover:bg-[#ECFEFF] rounded-lg" onClick={() => openEdit(crit)} data-testid={`edit-criteria-${crit.criteria_id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:bg-red-50 rounded-lg" onClick={() => handleDelete(crit.criteria_id)} data-testid={`delete-criteria-${crit.criteria_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {crit.description && <p className="text-sm text-slate-500 mb-3">{crit.description}</p>}
                <div className="space-y-2">
                  {(crit.levels || []).map((lvl, idx) => (
                    <div key={lvl.level} className={`flex items-start gap-3 rounded-2xl p-3 border ${LEVEL_COLORS[idx % LEVEL_COLORS.length]}`}>
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center font-extrabold text-sm flex-shrink-0 shadow-sm" style={{ fontFamily: 'Nunito' }}>
                        {lvl.level}
                      </div>
                      <p className="text-sm font-medium flex-1">{lvl.description || 'Not defined'}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>{editing ? 'Edit Criteria' : 'New Assessment Criteria'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="font-semibold text-slate-600">Criteria Title</Label>
              <Input data-testid="criteria-title-input" className="mt-1 rounded-xl" placeholder="e.g., Ability to Float" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Description (optional)</Label>
              <Textarea data-testid="criteria-desc-input" className="mt-1 rounded-xl" placeholder="Describe this criteria..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="font-semibold text-slate-600">
                  Levels ({form.levels.length})
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 rounded-full border-red-200 text-red-500 hover:bg-red-50"
                    onClick={removeLevel}
                    disabled={form.levels.length <= 2}
                    data-testid="remove-level-btn"
                    title="Remove last level"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-sm font-bold text-slate-500 min-w-[2rem] text-center">{form.levels.length}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 rounded-full border-green-200 text-green-600 hover:bg-green-50"
                    onClick={addLevel}
                    data-testid="add-level-btn"
                    title="Add level"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {form.levels.map((lvl, idx) => (
                  <div key={lvl.level} className={`rounded-2xl p-3 border ${LEVEL_COLORS[idx % LEVEL_COLORS.length]}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center font-extrabold text-sm shadow-sm">{lvl.level}</span>
                      <span className="text-xs font-bold uppercase tracking-wider">Level {lvl.level}</span>
                      {idx === 0 && <span className="text-[10px] opacity-60 font-medium">(Beginner)</span>}
                      {idx === form.levels.length - 1 && <span className="text-[10px] opacity-60 font-medium">(Mastery)</span>}
                    </div>
                    <Input
                      className="rounded-xl bg-white/80"
                      placeholder={`Describe level ${lvl.level}...`}
                      value={lvl.description}
                      onChange={e => updateLevel(idx, e.target.value)}
                      data-testid={`level-${lvl.level}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="save-criteria-btn" onClick={handleSave} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold gap-2">
              <Save className="w-4 h-4" /> {editing ? 'Update Criteria' : 'Create Criteria'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
